/* eslint-disable sonarjs/cognitive-complexity */
import React, { useState } from 'react';
import {
  ErrorMessage,
  InputContainer,
  InputField,
  InputIcon,
  InputLabel,
  InputWrapper,
  RequiredAsterisk,
} from './common-input-style';

const CommonInput = ({
  label,
  placeholder,
  type = 'text',
  value,
  onChange,
  icon,
  required = false,
  name,
  mask,
  disabled = false,
  error,
  onBlur,
  validateDate = false,
  ...props
}) => {
  const [isFocused, setIsFocused] = useState(false);

  const validateDateValue = (dateString) => {
    const numbers = dateString.replace(/\D/g, '');

    if (numbers.length !== 8) {
      return 'Data incompleta';
    }

    const day = parseInt(numbers.slice(0, 2));
    const month = parseInt(numbers.slice(2, 4));
    const year = parseInt(numbers.slice(4, 8));

    const currentYear = new Date().getFullYear();
    if (year < 1900 || year > currentYear) {
      return `Ano deve estar entre 1900 e ${currentYear}`;
    }

    if (month < 1 || month > 12) {
      return 'Mês inválido';
    }

    const daysInMonth = new Date(year, month, 0).getDate();
    if (day < 1 || day > daysInMonth) {
      return 'Dia inválido para o mês informado';
    }

    const inputDate = new Date(year, month - 1, day);
    inputDate.setHours(0, 0, 0, 0);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (inputDate > today) {
      return 'Data não pode ser futura';
    }

    return null;
  };

  const applyMask = (value, maskType) => {
    if (!maskType) return value;
    const numbers = value.replace(/\D/g, '');

    switch (maskType) {
      case 'cnpj':
        const cnpjNumbers = numbers.slice(0, 14);
        if (cnpjNumbers.length === 0) return '';
        if (cnpjNumbers.length <= 2) return cnpjNumbers;
        if (cnpjNumbers.length <= 5) {
          return `${cnpjNumbers.slice(0, 2)}.${cnpjNumbers.slice(2)}`;
        }
        if (cnpjNumbers.length <= 8) {
          return `${cnpjNumbers.slice(0, 2)}.${cnpjNumbers.slice(
            2,
            5,
          )}.${cnpjNumbers.slice(5)}`;
        }
        if (cnpjNumbers.length <= 12) {
          return `${cnpjNumbers.slice(0, 2)}.${cnpjNumbers.slice(
            2,
            5,
          )}.${cnpjNumbers.slice(5, 8)}/${cnpjNumbers.slice(8)}`;
        }
        return `${cnpjNumbers.slice(0, 2)}.${cnpjNumbers.slice(
          2,
          5,
        )}.${cnpjNumbers.slice(5, 8)}/${cnpjNumbers.slice(
          8,
          12,
        )}-${cnpjNumbers.slice(12, 14)}`;

      case 'phone':
        const phoneNumbers = numbers.slice(0, 11);
        if (phoneNumbers.length === 0) return '';
        if (phoneNumbers.length <= 2) return `(${phoneNumbers}`;
        if (phoneNumbers.length <= 6) {
          return `(${phoneNumbers.slice(0, 2)}) ${phoneNumbers.slice(2)}`;
        }
        if (phoneNumbers.length <= 10) {
          return `(${phoneNumbers.slice(0, 2)}) ${phoneNumbers.slice(
            2,
            6,
          )}-${phoneNumbers.slice(6)}`;
        }
        return `(${phoneNumbers.slice(0, 2)}) ${phoneNumbers.slice(
          2,
          7,
        )}-${phoneNumbers.slice(7, 11)}`;

      case 'cpf':
        const cpfNumbers = numbers.slice(0, 11);
        if (cpfNumbers.length === 0) return '';
        if (cpfNumbers.length <= 3) return cpfNumbers;
        if (cpfNumbers.length <= 6) {
          return `${cpfNumbers.slice(0, 3)}.${cpfNumbers.slice(3)}`;
        }
        if (cpfNumbers.length <= 9) {
          return `${cpfNumbers.slice(0, 3)}.${cpfNumbers.slice(
            3,
            6,
          )}.${cpfNumbers.slice(6)}`;
        }
        return `${cpfNumbers.slice(0, 3)}.${cpfNumbers.slice(
          3,
          6,
        )}.${cpfNumbers.slice(6, 9)}-${cpfNumbers.slice(9, 11)}`;

      case 'date':
        const dateNumbers = numbers.slice(0, 8);
        if (dateNumbers.length === 0) return '';
        if (dateNumbers.length <= 2) return dateNumbers;
        if (dateNumbers.length <= 4) {
          return `${dateNumbers.slice(0, 2)}/${dateNumbers.slice(2)}`;
        }
        return `${dateNumbers.slice(0, 2)}/${dateNumbers.slice(
          2,
          4,
        )}/${dateNumbers.slice(4, 8)}`;

      default:
        return value;
    }
  };

  const handleChange = (e) => {
    let newValue = e.target.value;

    if (mask) {
      newValue = applyMask(newValue, mask);
    }

    if (onChange) {
      onChange({
        ...e,
        target: {
          ...e.target,
          name,
          value: newValue,
        },
      });
    }
  };

  const handleBlurInternal = (e) => {
    setIsFocused(false);

    if (validateDate && mask === 'date' && e.target.value) {
      const dateError = validateDateValue(e.target.value);

      if (dateError) {
        const customEvent = {
          ...e,
          target: {
            ...e.target,
            validationError: dateError,
          },
        };

        if (onBlur) {
          onBlur(customEvent);
        }
        return;
      }
    }

    if (onBlur) {
      onBlur(e);
    }
  };

  return (
    <InputWrapper>
      {label && (
        <InputLabel>
          {label}
          {required && <RequiredAsterisk> *</RequiredAsterisk>}
        </InputLabel>
      )}
      <InputContainer
        isFocused={isFocused}
        isDisabled={disabled}
        hasError={!!error}
      >
        {icon && <InputIcon isDisabled={disabled}>{icon}</InputIcon>}
        <InputField
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          onFocus={() => setIsFocused(true)}
          onBlur={handleBlurInternal}
          name={name}
          disabled={disabled}
          maxLength={mask === 'date' ? 10 : undefined}
          inputMode={mask === 'date' ? 'numeric' : undefined}
          {...props}
        />
      </InputContainer>
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </InputWrapper>
  );
};

export default CommonInput;
